/**
 * Jeff Shoes - Product Main JavaScript
 * Handles variant selection, gallery synchronization, size guide modals,
 * and ajax cart integration.
 */
window.jeffRenderCartDrawer ||= async (cartDrawer, parsedState) => {
  const sectionsToRender = cartDrawer?.getSectionsToRender?.() || [];
  const hasDrawerSection = parsedState?.sections?.['cart-drawer'];
  let state = parsedState;

  if (!hasDrawerSection && sectionsToRender.length) {
    const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    const refreshUrl = new URL(cartUrl, window.location.origin);
    refreshUrl.searchParams.set('sections', sectionsToRender.map(section => section.id).join(','));
    refreshUrl.searchParams.set('sections_url', window.location.pathname);
    const refreshResponse = await fetch(refreshUrl.toString(), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    const refreshedSections = await refreshResponse.json();
    state = { ...parsedState, sections: refreshedSections };
  }

  cartDrawer.classList.remove('is-empty');
  cartDrawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
  cartDrawer.renderContents(state);
};

(() => {
  if (customElements.get('jeff-product-main')) return;

  class JeffProductMain extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this.initialized) return;
      if (this.querySelector('[data-jeff-product-json]')) {
        this.init();
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;
      this.initialized = true;

      // Data
      const dataEl = this.querySelector('[data-jeff-product-json]');
      if (dataEl) {
        try {
          this.productData = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('Jeff Shoes: Error parsing product data', e);
          this.productData = null;
        }
      }

      if (!this.productData || !Array.isArray(this.productData.variants)) {
        this.productData = { variants: [] };
      }

      // Safe option indices determination
      this.colorIndex = -1;
      this.sizeIndex = -1;

      if (this.dataset.colorIndex !== undefined && this.dataset.colorIndex !== '') {
        const parsed = parseInt(this.dataset.colorIndex, 10);
        if (!isNaN(parsed) && parsed >= 0) this.colorIndex = parsed;
      }
      if (this.dataset.sizeIndex !== undefined && this.dataset.sizeIndex !== '') {
        const parsed = parseInt(this.dataset.sizeIndex, 10);
        if (!isNaN(parsed) && parsed >= 0) this.sizeIndex = parsed;
      }

      if ((this.colorIndex < 0 || this.sizeIndex < 0) && Array.isArray(this.productData?.options)) {
        this.productData.options.forEach((opt, idx) => {
          const optName = (typeof opt === 'string' ? opt : (opt.name || '')).toLowerCase();
          if (this.colorIndex < 0 && (optName.includes('color') || optName.includes('colour') || optName.includes('tono'))) {
            this.colorIndex = idx;
          }
          if (this.sizeIndex < 0 && (optName.includes('talla') || optName.includes('size') || optName.includes('número') || optName.includes('talle') || optName.includes('calzado'))) {
            this.sizeIndex = idx;
          }
        });
      }

      if (this.colorIndex < 0 && this.sizeIndex < 0) {
        this.colorIndex = 0;
        this.sizeIndex = 1;
      } else if (this.colorIndex < 0) {
        this.colorIndex = this.sizeIndex === 0 ? 1 : 0;
      } else if (this.sizeIndex < 0) {
        this.sizeIndex = this.colorIndex === 0 ? 1 : 0;
      }

      this.currentVariantId = parseInt(this.dataset.currentVariantId, 10);
      this.currentVariant = this.productData.variants.find(variant => variant.id === this.currentVariantId) || null;
      this.selectedOptions = {
        color: this.dataset.selectedColor || '',
        size: this.dataset.selectedSize || ''
      };
      this.requiresSizeSelection = this.dataset.requireSizeSelection === 'true';
      this.initialVariantAvailable = this.dataset.initialVariantAvailable === 'true';
      this.motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollBehavior = this.motionReduced ? 'auto' : 'smooth';
      this.lastFocusedElement = null;

      // Elements
      this.form = this.querySelector('[data-jeff-product-form]');
      this.variantIdInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.atcButton = this.querySelector('[data-jeff-atc-button]');
      this.atcButtonText = this.querySelector('[data-jeff-atc-text]');
      this.sizeFeedback = this.querySelector('[data-jeff-size-feedback]');
      this.sizePicker = this.querySelector('.jeff-option-group--size');
      this.formFeedback = this.querySelector('[data-jeff-form-feedback]');

      // Gallery elements
      this.gallery = this.querySelector('[data-jeff-gallery]');
      this.galleryTrack = this.querySelector('[data-jeff-gallery-track]');
      this.galleryItems = this.querySelectorAll('[data-jeff-gallery-item]');
      this.galleryCounter = this.querySelector('[data-jeff-gallery-counter]');
      this.galleryDots = this.querySelectorAll('[data-jeff-gallery-dot]');
      this.galleryThumbs = this.querySelectorAll('[data-jeff-gallery-thumb]');
      this.lightboxModal = this.querySelector('#jeff-lightbox-modal');
      this.activeModal = null;

      this.bindEvents();
      this.watchAddiWidget();
      this.updateColorButtonsState();
      this.updateSizeButtonsState();
      this.updatePurchaseReadiness(
        this.initialVariantAvailable && (!this.requiresSizeSelection || Boolean(this.selectedOptions.size))
      );
      this.selectInitialVariantMedia();
      this.initDeliveryDates();
    }

    bindEvents() {
      // Color swatches
      this.querySelectorAll('[data-jeff-color-swatch]').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          const color = swatch.dataset.value;
          this.onColorChange(color, swatch);
        });
      });

      // Size buttons
      this.querySelectorAll('[data-jeff-size-button]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (btn.disabled || btn.classList.contains('is-sold-out')) return;
          const size = btn.dataset.value;
          this.onSizeChange(size, btn);
        });
      });

      this.querySelectorAll('[role="radiogroup"]').forEach(group => {
        group.addEventListener('keydown', event => this.handleRadioGroupKeydown(event, group));
      });


      // Combo Offer Pill click
      this.querySelectorAll('[data-jeff-scroll-to-combo]').forEach(pill => {
        const scrollToCombo = (e) => {
          const comboEl = document.querySelector('jeff-pair-combo, .jeff-pair-combo, .jeff-block--combo-builder');
          if (comboEl) {
            e.preventDefault();
            comboEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            comboEl.classList.add('jeff-combo--highlighted');
            setTimeout(() => comboEl.classList.remove('jeff-combo--highlighted'), 1500);
            const tier2 = comboEl.querySelector('[data-tier="2"], [data-tier-card="2"], input[value="2"]');
            if (tier2) {
              tier2.click();
            }
          }
        };
        pill.addEventListener('click', scrollToCombo);
        pill.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            scrollToCombo(e);
          }
        });
      });

      // Main Form Ajax Submit
      if (this.form) {
        this.form.addEventListener('submit', this.handleAddToCart.bind(this));
      }

      // Gallery slider scroll event for mobile dots & counter with rAF throttling
      if (this.galleryTrack) {
        let isGalleryScrolling = false;
        this.galleryTrack.addEventListener('scroll', () => {
          if (!isGalleryScrolling) {
            isGalleryScrolling = true;
            window.requestAnimationFrame(() => {
              this.updateGalleryActiveSlide();
              isGalleryScrolling = false;
            });
          }
        }, { passive: true });
      }

      // Thumbnails click
      this.querySelectorAll('[data-jeff-gallery-thumb]').forEach(thumb => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const mediaId = thumb.dataset.mediaId;
          let targetIndex = -1;
          if (mediaId) {
            targetIndex = Array.from(this.galleryItems).findIndex(item => item.dataset.mediaId === mediaId);
          }
          if (targetIndex === -1) {
            const parent = thumb.parentElement;
            targetIndex = Array.from(parent.children).indexOf(thumb);
          }
          if (targetIndex !== -1) {
            this.scrollToSlide(targetIndex);
          }
        });
      });

      // Dots click
      this.galleryDots.forEach((dot, index) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          this.scrollToSlide(index);
        });
      });

      // Quantity selector buttons (+ / -)
      this.querySelectorAll('[data-jeff-qty-btn]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const action = btn.dataset.jeffQtyBtn;
          const input = this.querySelector('[data-jeff-qty-input]');
          if (!input) return;
          let currentVal = parseInt(input.value, 10) || 1;
          if (action === 'decrease' && currentVal > 1) {
            input.value = currentVal - 1;
          } else if (action === 'increase') {
            input.value = currentVal + 1;
          }
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      // Lightbox Zoom triggers
      this.querySelectorAll('[data-jeff-zoom-trigger]').forEach(mediaWrap => {
        mediaWrap.addEventListener('click', () => {
          const zoomUrl = mediaWrap.dataset.jeffZoomTrigger;
          const lightboxModal = this.lightboxModal || document.getElementById('jeff-lightbox-modal');
          const lightboxImg = lightboxModal ? lightboxModal.querySelector('[data-jeff-lightbox-img]') : null;
          if (lightboxModal && lightboxImg && zoomUrl) {
            lightboxImg.src = zoomUrl;
            this.openModal(lightboxModal, mediaWrap);
          }
        });
      });

      // Smooth scroll for in-page anchors (e.g. rating -> reviews)
      this.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const href = anchor.getAttribute('href');
          if (href && href.length > 1) {
            const cleanId = href.replace('#', '');
            const target = document.getElementById(cleanId) || document.querySelector(`[id*="${cleanId}"]`) || document.querySelector(href);
            if (target) {
              e.preventDefault();
              target.scrollIntoView({ behavior: this.scrollBehavior, block: 'start' });
            }
          }
        });
      });

      // Modals triggers
      this.querySelectorAll('[data-jeff-modal-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetModalId = btn.dataset.jeffModalOpen;
          if (targetModalId === 'jeff-measure-foot-modal') {
            const guideModal = this.querySelector('#jeff-size-guide-modal') || document.querySelector('#jeff-size-guide-modal');
            if (guideModal) {
              this.openModal(guideModal, btn);
              this.switchSizeGuideTab(guideModal, 'measure');
              return;
            }
          }
          const modal = this.querySelector(`#${CSS.escape(targetModalId)}`) || document.querySelector(`#${CSS.escape(targetModalId)}`);
          if (modal) {
            this.openModal(modal, btn);
            if (targetModalId === 'jeff-size-guide-modal') {
              this.switchSizeGuideTab(modal, 'table');
              this.syncSizeGuideSelection(modal);
            }
          }
        });
      });

      // Modals close
      this.querySelectorAll('[data-jeff-modal-close]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const modal = btn.closest('.jeff-modal');
          if (modal) {
            this.closeModal(modal);
          }
        });
      });

      // Modal overlay click
      this.querySelectorAll('.jeff-modal').forEach(modal => {
        modal.addEventListener('click', (e) => {
          if (e.target.classList.contains('jeff-modal__overlay')) {
            this.closeModal(modal);
          }
        });
      });

      // Size guide tabs, calculator & selection
      this.querySelectorAll('.jeff-modal--size-guide').forEach(modal => {
        modal.querySelectorAll('[data-jeff-tab]').forEach(tabBtn => {
          tabBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.switchSizeGuideTab(modal, tabBtn.dataset.jeffTab);
          });
        });

        modal.querySelectorAll('[data-jeff-switch-tab]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.switchSizeGuideTab(modal, btn.dataset.jeffSwitchTab);
          });
        });

        // Gender switch buttons (Mujer / Hombre)
        modal.querySelectorAll('[data-jeff-gender-btn]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.switchSizeGuideGender(modal, btn.dataset.jeffGenderBtn);
          });
        });

        modal.querySelectorAll('[data-jeff-calc-select]').forEach(calcSelect => {
          calcSelect.addEventListener('change', () => {
            const sizeVal = calcSelect.value;
            if (!sizeVal) return;
            this.highlightSizeGuideRow(modal, sizeVal);
            const opt = calcSelect.selectedOptions[0];
            const cm = opt ? opt.dataset.cm : '';
            const feedback = modal.querySelector('[data-jeff-calc-feedback]');
            if (feedback) {
              feedback.innerHTML = `✨ Talla sugerida: <strong>Talla ${sizeVal}</strong> (${cm} cm)`;
            }
          });
        });

        // Table pick buttons (One-click selection directly into PDP)
        modal.querySelectorAll('[data-pick-size]').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const pickedSize = btn.dataset.pickSize;
            this.selectSizeFromGuide(pickedSize);
            this.highlightSizeGuideRow(modal, pickedSize);
            setTimeout(() => {
              this.closeModal(modal);
            }, 260);
          });
        });

        // Make entire table row tappable for effortless mobile selection (Baymard UX)
        modal.querySelectorAll('[data-size-row]').forEach(row => {
          row.addEventListener('click', (e) => {
            if (e.target instanceof Element && e.target.closest('[data-pick-size]')) return;
            const pickedSize = row.dataset.sizeRow;
            if (!pickedSize) return;
            this.selectSizeFromGuide(pickedSize);
            this.highlightSizeGuideRow(modal, pickedSize);
            setTimeout(() => {
              this.closeModal(modal);
            }, 260);
          });
        });

        // Zoom toggle for image tab
        const toggleZoom = () => {
          const imgFigure = modal.querySelector('.jeff-size-guide__image');
          if (!imgFigure) return;
          const isZoomed = imgFigure.classList.toggle('is-zoomed');
          const zoomBtn = modal.querySelector('[data-jeff-zoom-toggle]');
          if (zoomBtn) {
            zoomBtn.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
            const label = zoomBtn.querySelector('.jeff-modal__zoom-label');
            if (label) label.textContent = isZoomed ? 'Reducir' : 'Ampliar';
          }
        };

        const zoomBtn = modal.querySelector('[data-jeff-zoom-toggle]');
        if (zoomBtn) {
          zoomBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleZoom();
          });
        }

        const imgFigure = modal.querySelector('.jeff-size-guide__image');
        if (imgFigure) {
          imgFigure.addEventListener('click', (e) => {
            if (e.target instanceof Element && (e.target.closest('.jeff-size-guide__zoom-hint') || e.target.tagName === 'IMG' || e.target === imgFigure)) {
              toggleZoom();
            }
          });
          imgFigure.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleZoom();
            }
          });
        }
      });

      // Escape key to close modals
      window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openModal = this.activeModal || this.querySelector('.jeff-modal.is-open');
          if (openModal) {
            this.closeModal(openModal);
          }
        } else if (e.key === 'Tab') {
          const openModal = this.activeModal || this.querySelector('.jeff-modal.is-open');
          if (openModal) this.trapModalFocus(e, openModal);
        }
      });

      // Shopify Theme Editor block interaction
      document.addEventListener('shopify:block:select', (event) => {
        if (event.target && this.contains(event.target)) {
          event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }

    yieldToNextPaint(fn) {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          setTimeout(fn, 0);
        });
      } else {
        setTimeout(fn, 0);
      }
    }

    onColorChange(color, swatchEl) {
      this.selectedOptions.color = color;
      
      // 1. Immediate visual feedback (<10ms)
      const swatches = this.querySelectorAll('[data-jeff-color-swatch]');
      swatches.forEach(s => {
        const isMatch = s === swatchEl;
        s.classList.toggle('is-selected', isMatch);
        s.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        s.tabIndex = isMatch ? 0 : -1;
      });

      // 2. Update color label text
      const labelVal = this.querySelector('[data-jeff-color-name]');
      if (labelVal) labelVal.textContent = color;

      // 3. Yield heavy secondary work to the next frame to prevent blocking next paint (INP)
      this.yieldToNextPaint(() => {
        try {
          this.syncGalleryWithColor(color, swatchEl);
        } catch (err) {
          console.error('Jeff Shoes: Error syncing gallery with color', err);
        }

        try {
          this.updateSizeButtonsState();
        } catch (err) {
          console.error('Jeff Shoes: Error updating size buttons', err);
        }

        try {
          this.resolveCurrentVariant();
        } catch (err) {
          console.error('Jeff Shoes: Error resolving variant', err);
        }
      });
    }

    onSizeChange(size, btnEl) {
      // 1. Immediate visual feedback (<10ms)
      this.hasSizeError = false;
      clearTimeout(this.sizeErrorTimeout);
      this.atcButton?.classList.remove('has-size-error');
      this.querySelector('.jeff-sizes')?.classList.remove('has-size-error');
      if (this.atcButtonText) {
        this.atcButtonText.textContent = this.atcButton?.dataset.labelAdd || 'AGREGAR AL CARRITO';
      }
      if (this.sizeFeedback) {
        this.sizeFeedback.hidden = true;
        this.sizeFeedback.classList.remove('has-error');
      }

      this.selectedOptions.size = size;

      // Single-pass UI update on size buttons
      const sizeButtons = this.querySelectorAll('[data-jeff-size-button]');
      sizeButtons.forEach(b => {
        const isMatch = b === btnEl;
        b.classList.toggle('is-selected', isMatch);
        b.setAttribute('aria-checked', isMatch ? 'true' : 'false');
        b.tabIndex = isMatch ? 0 : -1;
      });

      const sizeName = this.querySelector('[data-jeff-size-name]');
      if (sizeName) sizeName.textContent = size;

      // 2. Yield non-critical variant calculations and URL updates to next paint (INP)
      this.yieldToNextPaint(() => {
        try {
          this.resolveCurrentVariant();
        } catch (err) {
          console.error('Jeff Shoes: Error resolving variant', err);
        }
      });
    }

    switchSizeGuideTab(modal, tabName) {
      if (!modal) return;
      modal.querySelectorAll('[data-jeff-tab]').forEach(tab => {
        const isActive = tab.dataset.jeffTab === tabName;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      modal.querySelectorAll('[data-jeff-panel]').forEach(panel => {
        const isMatch = panel.dataset.jeffPanel === tabName;
        panel.classList.toggle('is-active', isMatch);
        if (isMatch) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    }

    switchSizeGuideGender(modal, gender) {
      if (!modal || !gender) return;
      modal.querySelectorAll('[data-jeff-gender-btn]').forEach(btn => {
        const isTarget = btn.dataset.jeffGenderBtn === gender;
        btn.classList.toggle('is-active', isTarget);
        btn.setAttribute('aria-pressed', isTarget ? 'true' : 'false');
      });
      modal.querySelectorAll('[data-jeff-gender-table]').forEach(table => {
        const isTarget = table.dataset.jeffGenderTable === gender;
        table.hidden = !isTarget;
      });
      modal.querySelectorAll('[data-jeff-calc-select]').forEach(sel => {
        const isTarget = sel.dataset.calcGender === gender;
        sel.hidden = !isTarget;
        sel.disabled = !isTarget;
      });
      const activeCalc = modal.querySelector(`[data-jeff-calc-select][data-calc-gender="${gender}"]`);
      if (activeCalc) {
        const feedback = modal.querySelector('[data-jeff-calc-feedback]');
        if (feedback) {
          feedback.innerHTML = `<span class="jeff-size-calculator__result">Selecciona tu medida arriba o una fila abajo</span>`;
        }
      }
    }

    highlightSizeGuideRow(modal, sizeVal) {
      if (!modal || !sizeVal) return;
      const cleanSize = String(sizeVal).trim();
      modal.querySelectorAll('[data-size-row]').forEach(row => {
        const isSelected = row.dataset.sizeRow === cleanSize;
        row.classList.toggle('is-selected', isSelected);
        if (isSelected) {
          row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }

    syncSizeGuideSelection(modal) {
      if (!modal) return;
      const currentSize = this.selectedOptions?.size || '';
      const numSize = parseInt(currentSize, 10);
      if (!isNaN(numSize) && numSize >= 41) {
        this.switchSizeGuideGender(modal, 'hombre');
      } else if (!isNaN(numSize) && numSize <= 36) {
        this.switchSizeGuideGender(modal, 'mujer');
      }

      const calcSelect = modal.querySelector('[data-jeff-calc-select]:not([hidden]):not(:disabled)') || modal.querySelector('[data-jeff-calc-select]');
      const feedback = modal.querySelector('[data-jeff-calc-feedback]');

      if (currentSize) {
        this.highlightSizeGuideRow(modal, currentSize);
        if (calcSelect) {
          calcSelect.value = currentSize;
          const opt = calcSelect.selectedOptions[0];
          const cm = opt?.dataset?.cm || '';
          if (feedback) {
            feedback.innerHTML = `Tu talla seleccionada en tienda: <strong>Talla ${currentSize}</strong> ${cm ? `(${cm} cm)` : ''}`;
          }
        }
      } else {
        modal.querySelectorAll('[data-size-row]').forEach(row => row.classList.remove('is-selected'));
        if (calcSelect) calcSelect.value = '';
        if (feedback) {
          feedback.innerHTML = `<span class="jeff-size-calculator__result">Selecciona tu medida arriba o una fila abajo</span>`;
        }
      }
    }

    selectSizeFromGuide(size) {
      if (!size) return;
      const sizeBtn = this.querySelector(`[data-jeff-size-button][data-value="${CSS.escape(size)}"]`);
      if (sizeBtn) {
        sizeBtn.click();
      }
    }

    openModal(modal, trigger) {
      if (modal.classList.contains('jeff-modal--size-guide')) {
        this.syncSizeGuideSelection(modal);
      }
      this.lastFocusedElement = trigger || document.activeElement;
      if (modal.parentElement !== document.body) {
        modal.jeffPortalParent = modal.parentElement;
        modal.jeffPortalNextSibling = modal.nextSibling;
        document.body.appendChild(modal);
      }
      this.activeModal = modal;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('jeff-modal-open');
      document.body.classList.add('jeff-modal-open');
      document.body.style.overflow = 'hidden';
      const focusTarget = modal.querySelector('[data-jeff-modal-close], button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      window.requestAnimationFrame(() => focusTarget?.focus());
    }

    handleRadioGroupKeydown(event, group) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const options = Array.from(group.querySelectorAll('[role="radio"]:not(:disabled)'));
      if (!options.length) return;
      event.preventDefault();
      const currentIndex = Math.max(0, options.indexOf(document.activeElement));
      let nextIndex = currentIndex;
      if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = options.length - 1;
      else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
      else nextIndex = (currentIndex - 1 + options.length) % options.length;
      options[nextIndex].focus();
      options[nextIndex].click();
    }

    closeModal(modal) {
      const zoomedFigure = modal.querySelector('.jeff-size-guide__image.is-zoomed');
      if (zoomedFigure) {
        zoomedFigure.classList.remove('is-zoomed');
        const zoomBtn = modal.querySelector('[data-jeff-zoom-toggle]');
        if (zoomBtn) {
          zoomBtn.setAttribute('aria-pressed', 'false');
          const label = zoomBtn.querySelector('.jeff-modal__zoom-label');
          if (label) label.textContent = 'Ampliar';
        }
      }
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('jeff-modal-open');
      document.body.classList.remove('jeff-modal-open');
      document.body.style.overflow = '';
      if (modal.jeffPortalParent?.isConnected) {
        if (modal.jeffPortalNextSibling?.parentNode === modal.jeffPortalParent) {
          modal.jeffPortalParent.insertBefore(modal, modal.jeffPortalNextSibling);
        } else {
          modal.jeffPortalParent.appendChild(modal);
        }
      }
      this.activeModal = null;
      if (this.lastFocusedElement instanceof HTMLElement) this.lastFocusedElement.focus();
      this.lastFocusedElement = null;
    }

    trapModalFocus(event, modal) {
      const focusable = Array.from(modal.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'))
        .filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    selectInitialVariantMedia() {
      const variant = this.productData.variants.find(item => Number(item.id) === this.currentVariantId);
      const mediaId = variant?.featured_media?.id || variant?.featured_image?.id;
      if (!mediaId) return;
      const index = Array.from(this.galleryItems).findIndex(item => Number(item.dataset.mediaId) === Number(mediaId));
      if (index < 0) return;
      window.requestAnimationFrame(() => this.scrollToSlide(index, 'auto'));
    }

    getVariantColor(variant) {
      if (!variant || !Array.isArray(variant.options)) return '';
      if (this.colorIndex >= 0 && this.colorIndex < variant.options.length) {
        return (variant.options[this.colorIndex] || '').trim();
      }
      return (variant.options[0] || '').trim();
    }

    getVariantSize(variant) {
      if (!variant || !Array.isArray(variant.options)) return '';
      if (this.sizeIndex >= 0 && this.sizeIndex < variant.options.length) {
        return (variant.options[this.sizeIndex] || '').trim();
      }
      if (variant.options.length > 1) {
        return (variant.options[1] || '').trim();
      }
      return (variant.options[0] || '').trim();
    }

    updateSizeButtonsState() {
      const sizeButtons = this.querySelectorAll('[data-jeff-size-button]');
      const currentColor = (this.selectedOptions.color || '').trim().toLowerCase();

      sizeButtons.forEach(btn => {
        const sizeVal = (btn.dataset.value || '').trim().toLowerCase();
        // Check if there is any available variant with this color and size
        const variant = this.productData.variants.find(v => {
          const vColor = this.getVariantColor(v).toLowerCase();
          const vSize = this.getVariantSize(v).toLowerCase();

          const hasColor = !currentColor || vColor === currentColor || v.options.some(opt => (opt || '').trim().toLowerCase() === currentColor);
          const hasSize = vSize === sizeVal || v.options.some(opt => (opt || '').trim().toLowerCase() === sizeVal);
          return hasColor && hasSize;
        });

        const isSoldOut = !variant || !variant.available;
        if (isSoldOut && this.selectedOptions.size === btn.dataset.value) {
          this.selectedOptions.size = '';
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-checked', 'false');
          const label = this.querySelector('[data-jeff-size-name]');
          if (label) label.textContent = 'Selecciona tu talla';
        }
        btn.setAttribute('aria-label', `Talla ${btn.dataset.value}${isSoldOut ? ', agotada' : ''}`);
        btn.disabled = isSoldOut;
        btn.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');

        if (isSoldOut) {
          btn.classList.add('is-sold-out');
        } else {
          btn.classList.remove('is-sold-out');
        }
      });

      if (!Array.from(sizeButtons).some(button => button.tabIndex === 0 && !button.disabled)) {
        const firstAvailable = Array.from(sizeButtons).find(button => !button.disabled);
        if (firstAvailable) firstAvailable.tabIndex = 0;
      }


    }

    updateColorButtonsState() {
      const colorButtons = this.querySelectorAll('[data-jeff-color-swatch]');
      if (!colorButtons.length) return;

      colorButtons.forEach(btn => {
        const color = (btn.dataset.value || '').trim().toLowerCase();
        const hasAvailableVariant = this.productData.variants.some(variant => {
          const vColor = this.getVariantColor(variant).toLowerCase();
          const matchesColor = vColor === color || variant.options.some(opt => (opt || '').trim().toLowerCase() === color);
          return matchesColor && variant.available;
        });
        const isSoldOut = !hasAvailableVariant;

        btn.disabled = isSoldOut;
        btn.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
        btn.classList.toggle('is-sold-out', isSoldOut);
      });
    }

    resolveCurrentVariant() {
      const { color, size } = this.selectedOptions;
      if (!this.productData || !Array.isArray(this.productData.variants)) return;

      const cleanColor = (color || '').trim().toLowerCase();
      const cleanSize = (size || '').trim().toLowerCase();

      if (this.requiresSizeSelection && !cleanSize) {
        const colorVariants = this.productData.variants.filter(v => {
          const vColor = this.getVariantColor(v).toLowerCase();
          return !cleanColor || vColor === cleanColor || v.options.some(opt => (opt || '').trim().toLowerCase() === cleanColor);
        });
        const colorVariant = colorVariants.find(v => v.available) || colorVariants[0];

        if (colorVariant) {
          this.currentVariant = colorVariant;
          this.currentVariantId = colorVariant.id;
          if (this.variantIdInput) this.variantIdInput.value = colorVariant.id;
          this.updatePrice(colorVariant);
          const skuEl = this.querySelector('[data-jeff-sku]');
          if (skuEl && colorVariant.sku) skuEl.textContent = colorVariant.sku;
        }

        this.updatePurchaseReadiness(false);
        return;
      }

      // Case-insensitive match for variant options
      let variant = this.productData.variants.find(v => {
        const vColor = this.getVariantColor(v).toLowerCase();
        const vSize = this.getVariantSize(v).toLowerCase();

        const matchColor = !cleanColor || vColor === cleanColor || v.options.some(opt => (opt || '').trim().toLowerCase() === cleanColor);
        const matchSize = !cleanSize || vSize === cleanSize || v.options.some(opt => (opt || '').trim().toLowerCase() === cleanSize);
        return matchColor && matchSize;
      });

      if (!variant) {
        this.currentVariant = null;
        this.currentVariantId = null;
        if (this.variantIdInput) this.variantIdInput.value = '';
        this.updatePurchaseReadiness(false);
        return;
      }

      if (variant) {
        this.currentVariant = variant;
        this.currentVariantId = variant.id;
        if (this.variantIdInput) this.variantIdInput.value = variant.id;

        // Update URL safely
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {}

        // Update price safely
        this.updatePrice(variant);

        // Update SKU safely
        const skuEl = this.querySelector('[data-jeff-sku]');
        if (skuEl && variant.sku) skuEl.textContent = variant.sku;

        if (this.atcButtonText) {
          this.atcButtonText.textContent = variant.available
            ? (this.atcButton.dataset.labelAdd || 'AGREGAR AL CARRITO')
            : (this.atcButton.dataset.labelSoldOut || 'AGOTADO');
        }
        this.updatePurchaseReadiness(variant.available);
      }
    }

    updatePurchaseReadiness(isReady) {
      const ready = Boolean(isReady) && (!this.requiresSizeSelection || Boolean(this.selectedOptions.size));
      if (this.atcButton) {
        const selectedColor = (this.selectedOptions.color || '').trim().toLowerCase();
        const hasAvailableChoice = this.productData.variants.some(variant =>
          variant.available && (!selectedColor || this.getVariantColor(variant).toLowerCase() === selectedColor)
        );
        const needsSize = this.requiresSizeSelection && !this.selectedOptions.size;
        const isSoldOut = !hasAvailableChoice || (!needsSize && this.currentVariant && !this.currentVariant.available);
        this.atcButton.disabled = isSoldOut || (!needsSize && !ready);
        if (isSoldOut && this.atcButtonText) {
          this.atcButtonText.textContent = this.atcButton.dataset.labelSoldOut || 'AGOTADO';
        } else if (this.atcButtonText && !this.hasSizeError) {
          this.atcButtonText.textContent = this.atcButton.dataset.labelAdd || 'AGREGAR AL CARRITO';
        }
      }
      if (this.sizeFeedback) this.sizeFeedback.hidden = !this.hasSizeError;
      document.dispatchEvent(new CustomEvent('jeff:variant-change', {detail: {productId: this.dataset.productId, variantId: this.currentVariantId, ready}}));
    }

    updatePrice(variant) {
      if (!variant) return;
      const priceEls = this.querySelectorAll('[data-jeff-price]');
      const compareEls = this.querySelectorAll('[data-jeff-compare-price]');
      const cuotasEls = this.querySelectorAll('[data-jeff-cuota-val]');

      const formattedPrice = this.formatMoney(variant.price);
      priceEls.forEach(el => el.textContent = formattedPrice);

      if (variant.compare_at_price > variant.price) {
        const formattedCompare = this.formatMoney(variant.compare_at_price);
        compareEls.forEach(el => {
          el.textContent = formattedCompare;
          el.hidden = false;
        });

      } else {
        compareEls.forEach(el => el.hidden = true);

      }

      // Update 3 installments (cuotas)
      const cuotaMonto = Math.round(variant.price / 3);
      const formattedCuota = this.formatMoney(cuotaMonto);
      cuotasEls.forEach(el => el.textContent = formattedCuota);

      // Addi's Shopify wrapper inserts this custom element after the anchor.
      const addiWidget = this.querySelector('addi-product-widget');
      if (addiWidget) {
        addiWidget.setAttribute('price', variant.price / 100);
      }
    }

    watchAddiWidget() {
      const anchor = this.querySelector('[data-jeff-addi-anchor]');
      if (!anchor) return;

      const priceBlock = anchor.parentElement;
      const syncInitialPrice = () => {
        const widget = priceBlock.querySelector('addi-product-widget');
        if (!widget) return false;

        const variant = this.productData.variants.find(item => String(item.id) === String(this.currentVariantId));
        if (variant) widget.setAttribute('price', variant.price / 100);
        return true;
      };

      if (syncInitialPrice()) return;
      const observer = new MutationObserver(() => {
        if (syncInitialPrice()) observer.disconnect();
      });
      observer.observe(priceBlock, { childList: true });
    }

    syncGalleryWithColor(color, swatchEl) {
      if (!this.galleryItems || !this.galleryItems.length) return;

      const mediaId = swatchEl ? swatchEl.dataset.mediaId : null;
      const imageSrc = swatchEl ? swatchEl.dataset.imageSrc : null;
      const imageSrcset = swatchEl ? swatchEl.dataset.imageSrcset : null;
      const cleanColor = (color || '').trim().toLowerCase();

      // Extract image filename token if imageSrc exists
      let imageFilename = '';
      if (imageSrc) {
        try {
          const urlClean = imageSrc.split('?')[0];
          imageFilename = urlClean.split('/').pop().toLowerCase();
        } catch (e) {}
      }

      let targetIndex = -1;

      // 1. Try matching by mediaId
      if (mediaId) {
        targetIndex = Array.from(this.galleryItems).findIndex(item => item.dataset.mediaId === mediaId);
      }

      // 2. Try matching by image filename in item's data-image-src or currentSrc
      if (targetIndex === -1 && imageFilename) {
        targetIndex = Array.from(this.galleryItems).findIndex(item => {
          const itemSrc = (item.dataset.imageSrc || '').toLowerCase();
          const img = item.querySelector('img');
          const currentSrc = img ? (img.currentSrc || img.src || '').toLowerCase() : '';
          return itemSrc.includes(imageFilename) || currentSrc.includes(imageFilename);
        });
      }

      // 3. Try matching by mediaColor / media alt text
      if (targetIndex === -1 && cleanColor) {
        targetIndex = Array.from(this.galleryItems).findIndex(item => {
          const itemColor = (item.dataset.mediaColor || '').trim().toLowerCase();
          return itemColor && (itemColor === cleanColor || itemColor.includes(cleanColor) || cleanColor.includes(itemColor));
        });
      }

      // 4. Scroll to matching slide if found
      if (targetIndex !== -1) {
        this.scrollToSlide(targetIndex);
      } else if (imageSrc) {
        // Fallback: If no slide matched by ID/alt/filename, update first slide media
        const heroItem = this.querySelector('.jeff-gallery__item--hero') || this.galleryItems[0];
        if (heroItem) {
          const heroImg = heroItem.querySelector('img');
          if (heroImg) {
            heroImg.src = imageSrc;
            if (imageSrcset) heroImg.srcset = imageSrcset;
          }
          const zoomTrigger = heroItem.querySelector('[data-jeff-zoom-trigger]');
          if (zoomTrigger) {
            zoomTrigger.dataset.jeffZoomTrigger = imageSrc;
          }
        }
        this.scrollToSlide(0);
      }
    }

    scrollToSlide(index, behavior = this.scrollBehavior) {
      if (!this.galleryItems || !this.galleryItems[index]) return;
      const target = this.galleryItems[index];

      if (this.galleryTrack) {
        const isStacked = this.gallery && this.gallery.classList.contains('jeff-gallery--stacked') && window.innerWidth >= 990;

        if (isStacked) {
          target.scrollIntoView({
            behavior,
            block: 'nearest'
          });
        } else {
          // Scroll ONLY the track horizontally; NEVER scroll the window
          const trackWidth = this.galleryTrack.clientWidth;
          this.galleryTrack.scrollTo({
            left: index * trackWidth,
            behavior
          });
        }
      }

      this.updateGalleryNav(index);
    }

    updateGalleryActiveSlide() {
      if (!this.galleryTrack || !this.galleryItems.length) return;
      const isHorizontal = this.galleryTrack.scrollWidth > this.galleryTrack.clientWidth;
      if (!isHorizontal) return;

      const scrollLeft = this.galleryTrack.scrollLeft;
      const itemWidth = this.galleryItems[0]?.offsetWidth || this.galleryTrack.offsetWidth;
      const activeIndex = Math.round(scrollLeft / itemWidth);
      if (activeIndex !== this.currentGalleryIndex) {
        this.currentGalleryIndex = activeIndex;
        this.updateGalleryNav(activeIndex);
      }
    }

    updateGalleryNav(index) {
      if (this.galleryCounter && this.galleryItems.length) {
        this.galleryCounter.textContent = `${index + 1} / ${this.galleryItems.length}`;
      }
      this.galleryDots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      const activeItem = this.galleryItems[index];
      const activeMediaId = activeItem ? activeItem.dataset.mediaId : null;

      this.galleryThumbs.forEach((thumb, i) => {
        const isActive = (i === index) || (activeMediaId && thumb.dataset.mediaId === activeMediaId);
        thumb.classList.toggle('is-active', isActive);
        if (isActive && thumb.parentElement) {
          const container = thumb.parentElement;
          if (container.scrollWidth > container.clientWidth) {
            container.scrollTo({
              left: thumb.offsetLeft - container.offsetLeft - 16,
              behavior: this.scrollBehavior
            });
          }
        }
      });
    }

    async handleAddToCart(e) {
      e.preventDefault();
      if (this.requiresSizeSelection && !this.selectedOptions.size) {
        this.hasSizeError = true;
        this.updatePurchaseReadiness(false);

        // Feedback en el botón principal
        if (this.atcButtonText) this.atcButtonText.textContent = 'SELECCIONA TU TALLA';
        this.atcButton?.classList.add('has-size-error');

        // Resaltar opciones de talla
        const sizesWrap = this.querySelector('.jeff-sizes');
        sizesWrap?.classList.add('has-size-error');

        // Scroll suave y foco
        this.sizePicker?.scrollIntoView({ behavior: this.scrollBehavior, block: 'center' });
        const firstAvail = this.querySelector('[data-jeff-size-button]:not(:disabled):not(.is-sold-out)');
        if (firstAvail) firstAvail.focus({ preventScroll: true });

        if (this.sizeFeedback) {
          this.sizeFeedback.textContent = 'Por favor selecciona tu talla antes de continuar';
          this.sizeFeedback.hidden = false;
          this.sizeFeedback.classList.add('has-error');
        }

        clearTimeout(this.sizeErrorTimeout);
        this.sizeErrorTimeout = setTimeout(() => {
          this.hasSizeError = false;
          if (this.atcButtonText && !this.selectedOptions.size) {
            this.atcButtonText.textContent = this.atcButton.dataset.labelAdd || 'AGREGAR AL CARRITO';
          }
          this.atcButton?.classList.remove('has-size-error');
          sizesWrap?.classList.remove('has-size-error');
        }, 3200);

        return;
      }
      // The selected option may have changed before the deferred URL/price update runs.
      this.resolveCurrentVariant();
      if (this.atcButton.disabled || this.atcButton.classList.contains('is-loading')) return;

      this.atcButton.classList.add('is-loading');

      const formData = new FormData(this.form);
      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.setActiveElement?.(document.activeElement);
      if (cartDrawer?.getSectionsToRender) {
        formData.append('sections', cartDrawer.getSectionsToRender().map(section => section.id));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const item = await response.json();

        if (response.ok) {
          // Estado de éxito en botón principal
          this.atcButton.classList.remove('is-loading');
          this.atcButtonText.textContent = '¡AGREGADO!';
          if (this.formFeedback) this.formFeedback.textContent = 'Producto agregado al carrito.';



          // Dawn keeps the drawer and bubble in sync from the same sections response.
          if (cartDrawer) {
            try {
              await this.renderCartDrawer(cartDrawer, item);
            } catch (drawerError) {
              console.error('Jeff Shoes: Cart drawer refresh failed', drawerError);
              window.location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
              return;
            }
          } else {
            window.location.href = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
          }

          setTimeout(() => {
            if (this.atcButtonText) this.atcButtonText.textContent = this.atcButton.dataset.labelAdd || 'AGREGAR AL CARRITO';

          }, 2200);
        } else {
          throw new Error(item.description || 'Error al agregar al carrito');
        }
      } catch (err) {
        console.error('Jeff Shoes: Cart Add Error', err);
        if (this.formFeedback) this.formFeedback.textContent = err.message || 'No pudimos agregar el producto. Intenta de nuevo.';
        this.atcButton.classList.remove('is-loading');
        this.atcButtonText.textContent = 'ERROR - INTENTAR DE NUEVO';

        setTimeout(() => {
          if (this.atcButtonText) this.atcButtonText.textContent = this.atcButton.dataset.labelAdd || 'AGREGAR AL CARRITO';

        }, 3000);
      }
    }

    initDeliveryDates() {
      const deliveryEls = this.querySelectorAll('[data-jeff-delivery-est]');
      if (!deliveryEls.length) return;

      deliveryEls.forEach((deliveryEl) => {
        const minDays = parseInt(deliveryEl.dataset.minDays, 10) || 2;
        const maxDays = parseInt(deliveryEl.dataset.maxDays, 10) || 4;
        const cutoffHour = parseInt(deliveryEl.dataset.cutoffHour, 10) || 14;

        const calcBusinessDate = (daysToAdd) => {
          const d = new Date();
          if (d.getHours() >= cutoffHour) {
            d.setDate(d.getDate() + 1);
          }
          while (d.getDay() === 0 || d.getDay() === 6) {
            d.setDate(d.getDate() + 1);
          }
          let count = 0;
          while (count < daysToAdd) {
            d.setDate(d.getDate() + 1);
            const day = d.getDay();
            if (day !== 0 && day !== 6) {
              count++;
            }
          }
          return d;
        };

        const minDate = calcBusinessDate(minDays);
        const maxDate = calcBusinessDate(maxDays);

        const dayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
        const monthNames = [
          'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
        ];

        const minDayName = dayNames[minDate.getDay()];
        const minDayNum = minDate.getDate();
        const minMonth = monthNames[minDate.getMonth()];

        const maxDayName = dayNames[maxDate.getDay()];
        const maxDayNum = maxDate.getDate();
        const maxMonth = monthNames[maxDate.getMonth()];

        const datesEl = deliveryEl.hasAttribute('data-jeff-delivery-dates') ? deliveryEl : deliveryEl.querySelector('[data-jeff-delivery-dates]');
        if (datesEl) {
          if (minMonth === maxMonth) {
            datesEl.innerHTML = `Recíbelo entre el <strong>${minDayName} ${minDayNum}</strong> y el <strong>${maxDayName} ${maxDayNum} de ${maxMonth}</strong>`;
          } else {
            datesEl.innerHTML = `Recíbelo entre el <strong>${minDayName} ${minDayNum} de ${minMonth}</strong> y el <strong>${maxDayName} ${maxDayNum} de ${maxMonth}</strong>`;
          }
        }
      });
    }

    async renderCartDrawer(cartDrawer, parsedState) {
      return window.jeffRenderCartDrawer(cartDrawer, parsedState);
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'COP';
      return new Intl.NumberFormat(document.documentElement.lang || 'es-CO', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'COP' ? 0 : 2
      }).format(cents / 100);
    }
  }

  customElements.define('jeff-product-main', JeffProductMain);
})();
